import React, { useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Phone } from 'lucide-react';
import './MDRTEquation.scss';

// "輕鬆MDRT 方程式": the recruitment illustration redrawn as SVG shapes with live text on top.
// All coordinates are in the artwork's own units; the whole thing scales with its container.
const VIEW_W = 1400;
// The artwork is cropped to y 150..830; the title is real markup above it.
const VIEW_Y = 150;
const VIEW_H = 680;
const pct = (value: number, total: number) => `${(value / total) * 100}%`;
const place = (x: number, y: number, w: number, h: number, fontSize?: number): React.CSSProperties => ({
  left: pct(x, VIEW_W),
  top: pct(y - VIEW_Y, VIEW_H),
  width: pct(w, VIEW_W),
  height: pct(h, VIEW_H),
  ...(fontSize ? { fontSize: `calc(var(--u) * ${fontSize})` } : {})
});

// One artwork unit in px, so text stays in proportion with the shapes at any width.
// Measured before paint, so the first frame is already at the right size.
const useUnit = (): [React.RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [unit, setUnit] = useState(0.5);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => {
      if (node.clientWidth > 0) setUnit(node.clientWidth / VIEW_W);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, unit];
};

// Rounded speech bubble with a tail dropping from its bottom edge to (tipX, tipY).
const Bubble = ({
  x,
  y,
  width,
  height,
  tail: [baseLeft, baseRight, tipX, tipY]
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  tail: [number, number, number, number];
}) => (
  <g className="mdrt-eq-bubble">
    <rect x={x} y={y} width={width} height={height} rx="52" />
    <path d={`M${baseLeft} ${y + height} L${tipX} ${tipY} L${baseRight} ${y + height}`} />
    <line x1={baseLeft + 8} x2={baseRight - 8} y1={y + height} y2={y + height} />
  </g>
);

const PlusBadge = ({ x, y, r }: { x: number; y: number; r: number }) => (
  <g className="mdrt-eq-plus">
    <circle cx={x} cy={y} r={r} />
    <path d={`M${x - r * 0.5} ${y} H${x + r * 0.5} M${x} ${y - r * 0.5} V${y + r * 0.5}`} />
  </g>
);

const People = ({ x, y }: { x: number; y: number }) => (
  <g className="mdrt-eq-people" transform={`translate(${x} ${y})`}>
    <circle cx="9" cy="-33" r="7" />
    <rect x="1" y="-24" width="16" height="26" rx="6" />
    <circle cx="33" cy="-33" r="7" />
    <path d="M26 -24 H40 L45 2 H21 Z" />
  </g>
);

const Handset = ({ x, y, angle }: { x: number; y: number; angle: number }) => (
  <g className="mdrt-eq-phone" transform={`rotate(${angle} ${x + 17} ${y + 17})`}>
    <Phone x={x} y={y} width="34" height="34" fill="currentColor" strokeWidth="1.5" />
  </g>
);

const strong = (testId: string, value: string) => <strong data-testid={testId}>{value}</strong>;

interface MDRTEquationProps {
  t: (id: string, values?: Record<string, string | number>) => string;
  /** Already formatted for display (or a dash while an input is empty). */
  rate: string;
  ratio: string;
  commission: string;
  commissionMessageId: string;
  daily: string;
  weekly: string;
  year: string;
  cases: string;
  incomeLabel: string;
  income: string;
  months: number;
  /** Non-Chinese copy runs longer, so it is set a little smaller. */
  latin: boolean;
}

const MDRTEquation = ({
  t,
  rate,
  ratio,
  commission,
  commissionMessageId,
  daily,
  weekly,
  year,
  cases,
  incomeLabel,
  income,
  months,
  latin
}: MDRTEquationProps) => {
  const [ref, unit] = useUnit();
  // Latin copy runs longer than the Chinese the bubbles were drawn for, so set it a little smaller.
  const font = (size: number) => size * (latin ? 0.84 : 1);
  const compound = t('mdrt-cal-eq-stage-compound').split(' · ');
  return (
    <section className="mdrt-eq" aria-label={t('mdrt-cal-eq-title')}>
      <header className="mdrt-eq-header">
        <span className="mdrt-eq-mark" aria-hidden="true" />
        <h3>{t('mdrt-cal-eq-title')}</h3>
      </header>
      <div className="mdrt-eq-stage" ref={ref} style={{ '--u': `${unit}px` } as React.CSSProperties}>
        <svg className="mdrt-eq-art" viewBox={`0 ${VIEW_Y} ${VIEW_W} ${VIEW_H}`} aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id="mdrt-eq-seed" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" stopColor="#3df08a" />
              <stop offset="1" stopColor="#2d9bf7" />
            </linearGradient>
            <linearGradient id="mdrt-eq-compound" x1="0" y1="0" x2="0.6" y2="1">
              <stop offset="0" stopColor="#6a64ff" />
              <stop offset="1" stopColor="#9b3cf3" />
            </linearGradient>
          </defs>
          <polygon points="105,806 882,604 882,806" fill="url(#mdrt-eq-seed)" />
          <rect x="892" y="614" width="268" height="192" fill="#3a62f5" />
          <polygon points="1172,608 1390,528 1390,806 1172,806" fill="url(#mdrt-eq-compound)" />
          <path className="mdrt-eq-arrow" d="M108 803 L880 606 L1160 608 C1256 608 1322 574 1372 520" />
          <polygon className="mdrt-eq-arrowhead" points="1392,496 1350,506 1384,540" />

          <Bubble x={15} y={490} width={345} height={145} tail={[225, 335, 302, 688]} />
          <Bubble x={378} y={368} width={285} height={180} tail={[405, 472, 432, 592]} />
          <Bubble x={700} y={268} width={315} height={192} tail={[730, 800, 762, 500]} />
          <Bubble x={1032} y={178} width={355} height={262} tail={[1075, 1150, 1100, 498]} />

          <PlusBadge x={138} y={742} r={15} />
          <PlusBadge x={368} y={685} r={15} />
          <PlusBadge x={612} y={625} r={15} />
          <People x={176} y={752} />
          <People x={406} y={695} />
          <People x={651} y={635} />
          <Handset x={266} y={694} angle={-8} />
          <Handset x={503} y={634} angle={-8} />
          <Handset x={750} y={570} angle={-8} />
        </svg>

        <div className="mdrt-eq-assumptions" style={place(25, 172, 665, 130, font(38))}>
          <span>{t('mdrt-cal-eq-rate')}</span>
          <strong data-testid="mdrt-cal-eq-rate">{rate}</strong>
          <span>{t(commissionMessageId)}</span>
          <strong data-testid="mdrt-cal-eq-case">{commission}</strong>
        </div>

        <div className="mdrt-eq-box" style={place(15, 502, 345, 125, font(39))}>
          <span className="is-blue">{t('mdrt-cal-eq-daily')}</span>
          <span className="is-gray">
            <FormattedMessage id="mdrt-cal-eq-daily-line" values={{ n: strong('mdrt-cal-eq-daily', daily) }} />
          </span>
        </div>
        <div className="mdrt-eq-box" style={place(378, 385, 285, 150, font(33))}>
          <span className="is-blue">{t('mdrt-cal-eq-weekly')}</span>
          <span className="is-gray">
            <FormattedMessage id="mdrt-cal-eq-total-line" values={{ n: strong('mdrt-cal-eq-weekly', weekly) }} />
          </span>
        </div>
        <div className="mdrt-eq-box" style={place(700, 285, 315, 160, font(38))}>
          <span className="is-blue">{t('mdrt-cal-eq-year', { months })}</span>
          <span className="is-gray">
            <FormattedMessage id="mdrt-cal-eq-total-line" values={{ n: strong('mdrt-cal-eq-year', year) }} />
          </span>
        </div>
        <div className="mdrt-eq-box" style={place(1032, 190, 355, 240, font(36))}>
          <span className="is-gray">{t('mdrt-cal-eq-closing', { ratio })}</span>
          <span className="is-blue is-big">
            <FormattedMessage id="mdrt-cal-eq-cases-line" values={{ n: strong('mdrt-cal-eq-cases', cases) }} />
          </span>
          <span className="is-gray is-income" data-testid="mdrt-cal-eq-income">
            {incomeLabel}: <b className="is-blue is-big">{income}</b>
          </span>
        </div>

        <div className="mdrt-eq-box is-white" style={place(560, 722, 320, 70, font(44))}>
          {t('mdrt-cal-eq-stage-seed')}
        </div>
        <div className="mdrt-eq-box is-white" style={place(892, 722, 268, 70, font(40))}>
          {t('mdrt-cal-eq-stage-harvest')}
        </div>
        <div className="mdrt-eq-box is-white is-tight" style={place(1172, 640, 218, 160, font(32))}>
          {compound.map(line => (
            <span key={line}>{line}</span>
          ))}
        </div>
        <span className="mdrt-eq-box is-tier" style={place(940, 540, 200, 60, 38)}>
          MDRT
        </span>
        <span className="mdrt-eq-box is-tier" style={place(1110, 518, 170, 60, 38)}>
          COT
        </span>
        <span className="mdrt-eq-box is-tier" style={place(1215, 476, 170, 60, 38)}>
          TOT
        </span>
      </div>
    </section>
  );
};

export default MDRTEquation;
